import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import { ResetApiState, ResponseWrapper } from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Route, RouteStateTypes } from "./Route.util";

const name = "main-routes";
const extraActions = createExtraActions();

const RouteInitialState: RouteStateTypes = {
  createRoute: ResetApiState(null),
  resetCreateRoute: ResetApiState(null),
  editRoute: ResetApiState(null),
  resetEditRoute: ResetApiState(null),
  routes: ResetApiState([]),
  updateSubRoutes: ResetApiState(null),
  resetUpdateSubRoutes: ResetApiState(null),
  deleteSubRoute: ResetApiState(null),
  resetDeleteSubRoute: ResetApiState(null),

  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createRoute = createAsyncThunk<ResponseWrapper<Route>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateRoute = createAction(`/reset-create-${name}`);

  const editRoute = createAsyncThunk<ResponseWrapper<Route>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditRoute = createAction(`/reset-edit-${name}`);

  const updateSubRoutes = createAsyncThunk<ResponseWrapper<Route>, any>(
    `/pickup-dropoff-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .post(`/${name}/sub-route/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetUpdateSubRoutes = createAction(`/reset-sub-route-${name}`);

  const deleteSubRoute = createAsyncThunk<ResponseWrapper<Route>, any>(
    `/delete-sub-route`,
    async (id: any, thunkApi) =>
      axios
        .delete(`sub-route/${id}`)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDeleteSubRoute = createAction(`/reset-delete-dropoff-${name}`);

  const getRoutes = createAsyncThunk<Route[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const resetStates = createAction(`/reset-states`);

  return {
    createRoute,
    resetCreateRoute,
    editRoute,
    resetEditRoute,
    updateSubRoutes,
    resetUpdateSubRoutes,
    getRoutes,
    deleteSubRoute,
    resetDeleteSubRoute,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<RouteStateTypes>
) {
  return {
    ...createRouteReducer(),
    ...resetCreateRoute(),
    ...editRouteReducer(),
    ...resetEditRoute(),
    ...updateSubRoutes(),
    ...resetUpdateSubRoutes(),
    ...getRoutes(),
    ...deleteSubRoute(),
    ...resetDeleteSubRoute(),
    ...resetStatesReducer(),
  };

  function createRouteReducer() {
    return {
      ...builder.addCase(extraActions.createRoute.pending, (state, action) => {
        state.createRoute = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.createRoute.fulfilled,
        (state, action) => {
          state.createRoute = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.createRoute.rejected, (state, action) => {
        state.createRoute = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetCreateRoute() {
    return {
      ...builder.addCase(extraActions.resetCreateRoute, (state, action) => {
        state.resetCreateRoute = ResetApiState(true);
        state.createRoute = ResetApiState(null);
      }),
    };
  }

  function editRouteReducer() {
    return {
      ...builder.addCase(extraActions.editRoute.pending, (state, action) => {
        state.editRoute = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editRoute.fulfilled, (state, action) => {
        state.editRoute = {
          loading: false,
          payload: action.payload.message,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editRoute.rejected, (state, action) => {
        state.editRoute = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetEditRoute() {
    return {
      ...builder.addCase(extraActions.resetEditRoute, (state, action) => {
        state.resetEditRoute = ResetApiState(true);
        state.editRoute = ResetApiState(null);
      }),
    };
  }

  function updateSubRoutes() {
    return {
      ...builder.addCase(
        extraActions.updateSubRoutes.pending,
        (state, action) => {
          state.updateSubRoutes = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateSubRoutes.fulfilled,
        (state, action) => {
          state.updateSubRoutes = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateSubRoutes.rejected,
        (state, action) => {
          state.updateSubRoutes = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetUpdateSubRoutes() {
    return {
      ...builder.addCase(extraActions.resetUpdateSubRoutes, (state, action) => {
        state.resetUpdateSubRoutes = ResetApiState(true);
        state.updateSubRoutes = ResetApiState(null);
      }),
    };
  }

  function getRoutes() {
    return {
      ...builder.addCase(extraActions.getRoutes.pending, (state, action) => {
        state.routes = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getRoutes.fulfilled, (state, action) => {
        state.routes = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getRoutes.rejected, (state, action) => {
        state.routes = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function deleteSubRoute() {
    return {
      ...builder.addCase(
        extraActions.deleteSubRoute.pending,
        (state, action) => {
          state.deleteSubRoute = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteSubRoute.fulfilled,
        (state, action) => {
          state.deleteSubRoute = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteSubRoute.rejected,
        (state, action) => {
          state.deleteSubRoute = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteSubRoute() {
    return {
      ...builder.addCase(extraActions.resetDeleteSubRoute, (state, action) => {
        state.resetDeleteSubRoute = ResetApiState(true);
        state.deleteSubRoute = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createRoute = ResetApiState(null);
        state.resetCreateRoute = ResetApiState(null);
        state.updateSubRoutes = ResetApiState(null);
        state.resetUpdateSubRoutes = ResetApiState(null);
        state.routes = ResetApiState(null);
      }),
    };
  }
}

const RouteSlice = createSlice({
  name,
  initialState: RouteInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const RouteActions = { ...RouteSlice.actions, ...extraActions };
export const RouteReducer = RouteSlice.reducer;
